// src/store/slices/categoriesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getCategories,
  createCategory as createCategoryAPI,
  updateCategory as updateCategoryAPI,
  deleteCategory as deleteCategoryAPI,
  Category
} from 'api/assetsAPI';

// Use the EXACT same Category type from your API
export type { Category } from 'api/assetsAPI';

interface CategoryState {
  data: Category[];
  loading: boolean;
  error: string | null;
}

const initialState: CategoryState = {
  data: [],
  loading: false,
  error: null
};

// Async Thunks - using your exact API functions
export const fetchCategories = createAsyncThunk<
  Category[],
  void,
  { rejectValue: string }
>('categories/fetchCategories', async (_, { rejectWithValue }) => {
  try {
    return await getCategories();
  } catch (error: unknown) {
    const axiosError = error as {
      response?: {
        data?: {
          detail?: string | Array<{ msg?: string; message?: string }>;
          message?: string;
          error?: string;
        };
      };
      message?: string;
    };

    let errorMessage = 'Failed to fetch categories';

    // Extract backend error message
    if (axiosError.response?.data?.detail) {
      if (typeof axiosError.response.data.detail === 'string') {
        errorMessage = axiosError.response.data.detail;
      } else if (Array.isArray(axiosError.response.data.detail)) {
        const firstError = axiosError.response.data.detail[0];
        errorMessage = firstError?.msg || firstError?.message || errorMessage;
      }
    } else if (axiosError.response?.data?.message) {
      errorMessage = axiosError.response.data.message;
    } else if (axiosError.response?.data?.error) {
      errorMessage = axiosError.response.data.error;
    } else if (axiosError.message) {
      errorMessage = axiosError.message;
    }

    console.error('Fetch categories backend error:', axiosError.response?.data);
    return rejectWithValue(errorMessage);
  }
});

export const createCategory = createAsyncThunk<
  Category,
  { name: string; description?: string; company_id: number },
  { rejectValue: string }
>('categories/createCategory', async (categoryData, { rejectWithValue }) => {
  try {
    return await createCategoryAPI(categoryData);
  } catch (error: unknown) {
    const axiosError = error as {
      response?: {
        data?: {
          detail?: string | Array<{ msg?: string; message?: string }>;
          message?: string;
          error?: string;
        };
      };
      message?: string;
    };

    let errorMessage = 'Failed to create category';

    // Extract backend error message
    if (axiosError.response?.data?.detail) {
      if (typeof axiosError.response.data.detail === 'string') {
        errorMessage = axiosError.response.data.detail;
      } else if (Array.isArray(axiosError.response.data.detail)) {
        const firstError = axiosError.response.data.detail[0];
        errorMessage = firstError?.msg || firstError?.message || errorMessage;
      }
    } else if (axiosError.response?.data?.message) {
      errorMessage = axiosError.response.data.message;
    } else if (axiosError.response?.data?.error) {
      errorMessage = axiosError.response.data.error;
    } else if (axiosError.message) {
      errorMessage = axiosError.message;
    }

    console.error('Create category backend error:', axiosError.response?.data);
    return rejectWithValue(errorMessage);
  }
});

export const updateCategory = createAsyncThunk<
  Category,
  { categoryId: number; categoryData: { name: string; description?: string } },
  { rejectValue: string }
>(
  'categories/updateCategory',
  async ({ categoryId, categoryData }, { rejectWithValue }) => {
    try {
      return await updateCategoryAPI(categoryId, categoryData);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          data?: {
            detail?: string | Array<{ msg?: string; message?: string }>;
            message?: string;
            error?: string;
          };
        };
        message?: string;
      };

      let errorMessage = 'Failed to update category';

      // Extract backend error message
      if (axiosError.response?.data?.detail) {
        if (typeof axiosError.response.data.detail === 'string') {
          errorMessage = axiosError.response.data.detail;
        } else if (Array.isArray(axiosError.response.data.detail)) {
          const firstError = axiosError.response.data.detail[0];
          errorMessage = firstError?.msg || firstError?.message || errorMessage;
        }
      } else if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      } else if (axiosError.response?.data?.error) {
        errorMessage = axiosError.response.data.error;
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }

      console.error(
        'Update category backend error:',
        axiosError.response?.data
      );
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('categories/deleteCategory', async (categoryId, { rejectWithValue }) => {
  try {
    await deleteCategoryAPI(categoryId);
    return categoryId;
  } catch (error: unknown) {
    const axiosError = error as {
      response?: {
        data?: {
          detail?: string | Array<{ msg?: string; message?: string }>;
          message?: string;
          error?: string;
        };
      };
      message?: string;
    };

    let errorMessage = 'Failed to delete category';

    // Extract backend error message
    if (axiosError.response?.data?.detail) {
      if (typeof axiosError.response.data.detail === 'string') {
        errorMessage = axiosError.response.data.detail;
      } else if (Array.isArray(axiosError.response.data.detail)) {
        const firstError = axiosError.response.data.detail[0];
        errorMessage = firstError?.msg || firstError?.message || errorMessage;
      }
    } else if (axiosError.response?.data?.message) {
      errorMessage = axiosError.response.data.message;
    } else if (axiosError.response?.data?.error) {
      errorMessage = axiosError.response.data.error;
    } else if (axiosError.message) {
      errorMessage = axiosError.message;
    }

    console.error('Delete category backend error:', axiosError.response?.data);
    return rejectWithValue(errorMessage);
  }
});

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // All cases handle loading state the same way
      .addCase(fetchCategories.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.pending, state => {
        state.loading = true;
        state.error = null;
      })

      // Fulfilled cases
      .addCase(
        fetchCategories.fulfilled,
        (state, action: PayloadAction<Category[]>) => {
          state.loading = false;
          state.data = action.payload;
        }
      )
      .addCase(
        createCategory.fulfilled,
        (state, action: PayloadAction<Category>) => {
          state.loading = false;
          state.data.push(action.payload);
        }
      )
      .addCase(
        updateCategory.fulfilled,
        (state, action: PayloadAction<Category>) => {
          state.loading = false;
          const index = state.data.findIndex(
            cat => cat.id === action.payload.id
          );
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        }
      )
      .addCase(
        deleteCategory.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = false;
          state.data = state.data.filter(cat => cat.id !== action.payload);
        }
      )

      // Rejected cases
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch categories';
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create category';
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update category';
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete category';
      });
  }
});

export const { clearError } = categoriesSlice.actions;
export default categoriesSlice.reducer;

// Selectors
export const selectCategories = (state: { categories: CategoryState }) =>
  state.categories.data;
export const selectCategoriesLoading = (state: { categories: CategoryState }) =>
  state.categories.loading;
export const selectCategoriesError = (state: { categories: CategoryState }) =>
  state.categories.error;
